import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import LocationPicker from "../components/LocationPicker";
import VoiceRecorder from "../components/VoiceRecorder";
import LocationCandidatesModal from "../components/LocationCandidatesModal";
import { compressImage, formatDate } from "../lib/format";
import { toast } from "sonner";
import { Camera, Warning, CheckCircle, Sparkle } from "@phosphor-icons/react";

const STATUS_LABEL = {
  REPORTADO: "En revisión",
  VALIDADO: "Esperando aprobación",
  ESPERANDO_COMPRA: "En compras",
  ESPERANDO_REPUESTO: "Esperando repuesto",
  EN_MANTENIMIENTO: "En reparación",
  CONTRATISTA: "Contratista externo",
  CERRADO: "Resuelto",
  RECHAZADO: "Cerrado",
};

export function HousekeepingList() {
  const [reports, setReports] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/reports?scope=mine").then(r => setReports(r.data)); }, []);
  return (
    <AppShell title="Mis reportes">
      <button data-testid="new-report-fab" onClick={() => nav("/housekeeping/new")} className="q-btn-primary w-full md:w-auto mb-6 flex items-center justify-center gap-2">
        <Camera size={20} weight="fill" /> Nuevo reporte rápido
      </button>
      <div className="grid gap-3">
        {reports.length === 0 && <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Aún no tienes reportes. Toca "Nuevo reporte" para empezar.</div>}
        {reports.map(r => (
          <div key={r.id} role="button" tabIndex={0} onClick={() => nav(`/report/${r.id}`)}
               onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && nav(`/report/${r.id}`)}
               className="q-card p-4 flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-3 sm:gap-4 cursor-pointer" data-testid={`report-item-${r.room}`}>
            <div className="text-2xl font-mono font-light text-[color:var(--q-navy)] break-words min-w-0 basis-full sm:basis-auto sm:min-w-[3rem] sm:max-w-[12rem]">{r.room}</div>
            <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-2">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`chip chip-${r.severity} shrink-0`}>{r.severity.toUpperCase()}</span>
                <span className="text-xs text-[color:var(--q-muted)] whitespace-nowrap">{formatDate(r.created_at)}</span>
              </div>
              <div className="text-sm break-words">{r.note || "Sin nota"}</div>
            </div>
            <div className="text-right shrink-0 ml-auto sm:ml-0 order-2 sm:order-3">
              <div className="q-label text-[color:var(--q-muted)]">Estado</div>
              <div className={`text-xs font-semibold mt-0.5 ${r.status === "CERRADO" ? "text-emerald-600" : ""}`}>
                {r.status === "CERRADO" && <CheckCircle size={14} className="inline mr-1" weight="fill" />}
                {STATUS_LABEL[r.status] || r.status}
              </div>
            </div>
          </div>
        ))}
      </div>
    </AppShell>
  );
}

export function HousekeepingNew() {
  const { session } = useAuth();
  const [location, setLocation] = useState(null);
  const [severity, setSeverity] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientToken] = useState(() => crypto.randomUUID());
  const [voiceCandidates, setVoiceCandidates] = useState([]);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [showCandidates, setShowCandidates] = useState(false);
  const nav = useNavigate();

  const isProtected = session?.hotel?.license_status === "PROTEGIDA";

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
  };

  const handleVoiceResult = (res) => {
    if (!res) return;
    if (res.severity) setSeverity(res.severity);
    if (res.note) setNote(res.note);
    setVoiceTranscript(res.transcript || "");
    const cands = res.candidates || [];
    if (cands.length === 1 && (cands[0].confidence ?? 0) >= 0.75 && !cands[0].uncatalogued) {
      setLocation({ ...cands[0] });
    } else if (cands.length >= 1) {
      setVoiceCandidates(cands);
      setShowCandidates(true);
    } else if (res.transcript) {
      toast.info("Nota transcrita · elige la ubicación manualmente");
    }
  };

  const submit = async () => {
    if (loading) return;
    if (!location || !severity) { toast.error("Selecciona ubicación y severidad"); return; }
    setLoading(true);
    try {
      const payload = {
        room: location.value, severity, note,
        asset_id: location.asset_id || null,
        asset_hint: location.kind === "activo" ? location.value : null,
        uncatalogued: !!location.uncatalogued,
        client_token: clientToken,
      };
      const { data: rep } = await api.post("/reports", payload);
      if (file) {
        try {
          const compressed = await compressImage(file);
          const fd = new FormData(); fd.append("file", compressed);
          await api.post(`/reports/${rep.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          toast.warning("Reporte enviado, pero la foto no se pudo subir.");
          nav("/housekeeping"); return;
        }
      }
      toast.success("Reporte enviado. Gracias.");
      nav("/housekeeping");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error de red al enviar. Tus datos se conservan.");
    } finally { setLoading(false); }
  };

  if (isProtected) {
    return <AppShell title="Nuevo reporte">
      <div className="q-card p-6 text-center">
        <Warning size={32} className="mx-auto text-[color:var(--q-gold)]" />
        <p className="mt-2 text-sm">La licencia está PROTEGIDA. No se pueden crear reportes nuevos.</p>
      </div>
    </AppShell>;
  }

  return (
    <AppShell title="Reporte rápido">
      <div className="grid gap-6 max-w-2xl">
        <div>
          <div className="q-label mb-3 flex items-center gap-2">
            <Sparkle size={12} weight="fill" className="text-[color:var(--q-gold)]"/>
            Nota de voz (opcional, más rápido)
          </div>
          <VoiceRecorder onResult={handleVoiceResult}/>
        </div>
        <div>
          <div className="q-label mb-3">1 · Ubicación</div>
          <LocationPicker value={location} onChange={setLocation}/>
        </div>
        <div>
          <div className="q-label mb-3">2 · Foto</div>
          <label htmlFor="photo-input" className="q-camera-label" data-testid="camera-input-label">
            {preview ? <img src={preview} alt="preview" className="max-h-48 rounded-lg" /> : (
              <>
                <Camera size={44} weight="fill" />
                <div className="mt-3 font-semibold">Tomar foto</div>
                <div className="text-xs text-white/60 mt-1">Toca para abrir la cámara</div>
              </>
            )}
          </label>
          <input id="photo-input" data-testid="photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile}/>
        </div>
        <div>
          <div className="q-label mb-3">3 · Severidad</div>
          <div className="grid grid-cols-3 gap-2">
            {[{v:"leve",label:"Leve",desc:"Estético"},{v:"media",label:"Media",desc:"Uso limitado"},{v:"grave",label:"Grave",desc:"Fuera de servicio"}].map(s => (
              <button key={s.v} data-testid={`sev-${s.v}`} onClick={() => setSeverity(s.v)}
                className={`p-4 rounded-lg border text-left transition ${severity === s.v ? `chip-${s.v} border-current !bg-opacity-100` : "bg-white border-[color:var(--q-border)]"}`}>
                <div className="font-bold">{s.label}</div>
                <div className="text-xs mt-1 opacity-80">{s.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="q-label mb-3">Nota (opcional)</div>
          <textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: gotea desde ayer" className="w-full h-20 p-3 rounded-lg border border-[color:var(--q-border)] bg-white text-sm"/>
        </div>
        <button data-testid="submit-report-btn" onClick={submit} disabled={loading || !location || !severity} className="q-btn-primary h-14 text-lg">
          {loading ? "Enviando…" : "Enviar reporte"}
        </button>
      </div>
      <LocationCandidatesModal
        open={showCandidates}
        candidates={voiceCandidates}
        transcript={voiceTranscript}
        onPick={(c) => { setLocation(c); setShowCandidates(false); }}
        onClose={() => setShowCandidates(false)}
      />
    </AppShell>
  );
}
